import React, { useEffect, useMemo, useState, useTransition } from 'react';
import { createRoot } from 'react-dom/client';
import 'antd/dist/reset.css';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Collapse,
  ConfigProvider,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Flex,
  Input,
  Pagination,
  Row,
  Select,
  Segmented,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import zhCN from 'antd/locale/zh_CN';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  ClusterOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { zhibiTheme } from '../shared/theme';
import './styles.css';

const { Title, Paragraph, Text, Link } = Typography;

type Community = {
  id: number;
  name: string;
  display_name: string;
  keywords: string[];
  tags: string[];
  screen_names_count: number;
  raw_screen_names_count?: number;
  excluded_screen_names_count?: number;
  screen_names?: string[];
  is_scraping: boolean;
  updated_date: string;
  detail_url: string;
  is_merged: boolean;
  merged_into: {
    id: number;
    display_name: string;
    detail_url: string;
  } | null;
};

type CommunityListResponse = {
  query: string;
  page: number;
  page_size: number;
  total: number;
  num_pages: number;
  has_previous: boolean;
  has_next: boolean;
  summary: {
    total: number;
    monitoring: number;
  };
  communities: Community[];
};

type Topic = {
  Topic?: number;
  Count?: number;
  CustomName?: string;
};

type TopicResult = {
  topics?: Topic[];
  tweet_count?: number;
  quality?: {
    status?: string;
    warnings?: string[];
  };
};

type Task = {
  id: string;
  description: string;
  since: string;
  until: string;
  updated_date: string;
  updated_date_humanize: string;
  status: string;
  progress: number;
  retry_at: string;
  last_error: string;
  quality_status: string;
  tweet_count?: number | null;
  topic_count?: number | null;
  is_ready: boolean;
};

type EventAlert = {
  id: number;
  title: string;
  summary: string;
  community: {
    id: number;
    display_name: string;
    detail_url: string;
  };
  task_id: string;
  period_start: string;
  period_end: string;
  period_label: string;
  severity: 'info' | 'warning' | 'critical' | string;
  severity_label: string;
  status: 'open' | 'reviewed' | 'dismissed' | string;
  status_label: string;
  score: number;
  reason_codes: string[];
  reason_labels: string[];
  metrics: Record<string, number>;
  topics: EventAlertTopic[];
  topic_signature?: EventTopicSignature;
  signal_count?: number;
  cluster?: EventClusterLink | null;
  updated_at: string;
  created_at: string;
};

type EventTopicSignature = {
  primary_label?: string;
  labels?: string[];
  keywords?: string[];
  novel_keywords?: string[];
  signature_hash?: string;
  cluster_version?: string;
};

type EventClusterLink = {
  id: number;
  title: string;
  scope_level: string;
  scope_label: string;
  level_label?: string;
  level_rank?: number;
  escalation_score: number;
};

type EventAlertTopic = {
  source_topic_id?: number | string;
  label: string;
  tokens: string[];
  count: number;
  weight: number;
  baseline_weight: number;
  delta: number;
  is_new: boolean;
  similarity: number;
};

type EventAlertDetail = EventAlert & {
  baseline?: {
    period_count?: number;
    tweet_count_mean?: number;
    tweet_count_std?: number;
    topic_count_mean?: number;
    top1_weight_mean?: number;
    task_ids?: string[];
    themes?: Array<{
      key: string;
      label: string;
      tokens: string[];
      weight: number;
      observations: number;
    }>;
  };
  evidence?: {
    top_keywords?: string[];
  };
  signals?: EventSignal[];
  quality?: {
    status?: string;
    warnings?: string[];
  };
  detector_version?: string;
  review_note?: string;
  reviewed_at?: string;
  reviewed_by?: string;
};

type EventSignal = {
  id: number;
  title: string;
  summary: string;
  community: {
    id: number;
    display_name: string;
    detail_url: string;
  };
  alert_id: number;
  task_id: string;
  period_start: string;
  period_end: string;
  period_label: string;
  status: 'open' | 'reviewed' | 'dismissed' | string;
  status_label: string;
  score: number;
  topic_weight: number;
  baseline_weight: number;
  topic_delta: number;
  topic_novelty: number;
  contribution_to_alert: number;
  topic_signature?: EventTopicSignature;
  evidence?: {
    top_keywords?: string[];
    representative_tweet_ids?: string[];
    tweet_ids_sample?: string[];
    evidence_status?: string;
  };
  cluster?: EventClusterLink | null;
  updated_at: string;
  created_at: string;
};

type EventAlertListResponse = {
  page: number;
  page_size: number;
  total: number;
  num_pages: number;
  summary: {
    open: number;
    reviewed: number;
    dismissed: number;
    critical: number;
    warning: number;
    info: number;
  };
  alerts: EventAlert[];
};

type EventCluster = {
  id: number;
  title: string;
  summary: string;
  scope_level: 'local' | 'domain' | 'cross_domain' | 'global' | string;
  scope_label: string;
  level_label?: string;
  level_rank?: number;
  severity: 'info' | 'warning' | 'critical' | string;
  severity_label: string;
  status: 'open' | 'reviewed' | 'dismissed' | string;
  status_label: string;
  lifecycle_status: 'new' | 'continuing' | 'history' | 'unknown' | string;
  lifecycle_label: string;
  is_current_period: boolean;
  is_new_in_period: boolean;
  publication?: {
    state: 'auto_visible' | 'review_recommended' | 'reviewed' | 'hidden' | string;
    label: string;
    requires_review: boolean;
    description: string;
  };
  escalation_score: number;
  display_rank: number;
  topic_signature?: EventTopicSignature;
  metrics: Record<string, number | string>;
  evidence?: {
    top_keywords?: string[];
    communities?: Array<{ id: number; display_name: string; alert_score?: number }>;
    alert_ids?: number[];
    signal_ids?: number[];
    source_titles?: string[];
  };
  alert_count: number;
  signal_count: number;
  community_count: number;
  first_seen_at: string;
  latest_seen_at: string;
  created_at: string;
  updated_at: string;
};

type EventClusterDetail = EventCluster & {
  cluster_version?: string;
  signature_hash?: string;
  review_note?: string;
  reviewed_at?: string;
  reviewed_by?: string;
  signals?: EventSignal[];
  alerts?: EventAlert[];
};

type EventClusterListResponse = {
  page: number;
  page_size: number;
  total: number;
  num_pages: number;
  lifecycle: string;
  current_period_start: string;
  current_period_end: string;
  summary: {
    open: number;
    reviewed: number;
    dismissed: number;
    local: number;
    domain: number;
    cross_domain: number;
    global: number;
    current: number;
    new: number;
    continuing: number;
    history: number;
    all: number;
  };
  clusters: EventCluster[];
};

type CommunityDetailResponse = {
  community: Community & { screen_names: string[] };
  tasks: Task[];
  selected_task: Task | null;
  selected_result: TopicResult;
};

const emptyList: CommunityListResponse = {
  query: '',
  page: 1,
  page_size: 12,
  total: 0,
  num_pages: 1,
  has_previous: false,
  has_next: false,
  summary: { total: 0, monitoring: 0 },
  communities: [],
};

function readQueryParam(name: string, fallback = '') {
  return new URLSearchParams(window.location.search).get(name) ?? fallback;
}

function formatDate(value: string) {
  if (!value) return '未知';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('zh-CN', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function formatNumber(value: number | undefined) {
  return new Intl.NumberFormat('zh-CN').format(value ?? 0);
}

function formatPercent(value: number | undefined) {
  return `${(((value ?? 0) as number) * 100).toFixed(1)}%`;
}

function memberCountText(community: Community) {
  const active = community.screen_names_count;
  const total = community.raw_screen_names_count ?? active;
  const excluded = community.excluded_screen_names_count ?? Math.max(total - active, 0);
  if (excluded > 0 || total !== active) {
    return `总 ${formatNumber(total)} 位成员，其中 ${formatNumber(active)} 位有效成员`;
  }
  return `总 ${formatNumber(total)} 位成员`;
}

function severityColor(value: string) {
  if (value === 'critical') return 'red';
  if (value === 'warning') return 'gold';
  return 'blue';
}

function statusColor(value: string) {
  if (value === 'reviewed') return 'green';
  if (value === 'dismissed') return 'default';
  return 'orange';
}

function scopeColor(value: string) {
  if (value === 'global') return 'red';
  if (value === 'cross_domain') return 'magenta';
  if (value === 'domain') return 'purple';
  return 'blue';
}

function lifecycleColor(value: string) {
  if (value === 'new') return 'green';
  if (value === 'continuing') return 'orange';
  if (value === 'history') return 'default';
  return 'blue';
}

function titleSourceLabel(value?: string | number) {
  if (value === 'llm') return '大模型命名';
  if (value === 'llm_failed') return '规则命名（模型失败）';
  return '规则命名';
}

function publicationColor(value?: string) {
  if (value === 'reviewed') return 'green';
  if (value === 'review_recommended') return 'gold';
  if (value === 'hidden') return 'default';
  return 'cyan';
}

function CommunityListApp() {
  const [query, setQuery] = useState(readQueryParam('q'));
  const [draft, setDraft] = useState(readQueryParam('q'));
  const [page, setPage] = useState(Math.max(Number.parseInt(readQueryParam('page', '1'), 10) || 1, 1));
  const [response, setResponse] = useState<CommunityListResponse>(emptyList);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [isPending, startTransition] = useTransition();
  const isCommunityLoading = loading || isPending;

  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (page > 1) params.set('page', String(page));
    window.history.replaceState(null, '', params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname);
  }, [page, query]);

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({ page: String(page), page_size: '12' });
    if (query) params.set('q', query);
    setError('');
    setLoading(true);

    fetch(`/awareness/api/communities/?${params}`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<CommunityListResponse>;
      })
      .then((payload) => startTransition(() => setResponse(payload)))
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError('社群数据加载失败，请稍后刷新页面。');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [page, query]);

  function submit() {
    setQuery(draft.trim());
    setPage(1);
  }

  return (
    <ConfigProvider theme={zhibiTheme} locale={zhCN}>
      <div className="zhibi-react-page">
        <Card className="zhibi-hero-card">
          <Breadcrumb
            className="zhibi-page-nav"
            items={[
              { title: <a href="/">首页</a> },
              { title: '态势感知' },
              { title: '社群监测' },
            ]}
          />
          <Title level={2}>社群监测</Title>
          <Paragraph type="secondary">从已识别社群进入周期话题变化，监测中的社群会优先展示，并为事件预警提供历史基线和当前周期证据。</Paragraph>
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}><Card><Statistic title="系统社群" value={response.summary.total} prefix={<ClusterOutlined />} /></Card></Col>
          <Col xs={24} md={12}><Card><Statistic title="监测中" value={response.summary.monitoring} prefix={<TeamOutlined />} /></Card></Col>
        </Row>

        <Card className="zhibi-section-card">
          <Flex gap={12} wrap="wrap">
            <Input.Search
              allowClear
              enterButton="检索"
              prefix={<SearchOutlined />}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onSearch={submit}
              placeholder="搜索社群、标签、关键词或 Twitter handle，例如 #中亚、媒体、@account"
              className="aw-search-input"
            />
            {query && <Button onClick={() => { setDraft(''); setQuery(''); setPage(1); }}>清空</Button>}
          </Flex>
        </Card>

        {error && <Alert className="zhibi-alert" type="error" showIcon message={error} />}

        <Card className="zhibi-section-card">
          <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
            <div>
              <Text type="secondary">社群目录</Text>
              <Title level={3}>{query ? `“${query}” 的社群结果` : '全部社群'}</Title>
              {isCommunityLoading && <Text type="secondary">正在加载社群数据，请稍候。</Text>}
            </div>
            <Space>{isCommunityLoading && <Spin />}<Tag color="blue">共 {formatNumber(response.total)} 个</Tag></Space>
          </Flex>

          {isCommunityLoading ? (
            <div className="aw-community-loading">
              <Spin size="large" tip="正在加载社群数据，请稍候..." />
              <Text type="secondary">社群成员和监测状态正在汇总，数据量较大时可能需要几秒钟。</Text>
            </div>
          ) : response.communities.length === 0 ? (
            <Empty description="暂无社群结果，可以换一个关键词，或先从标签、社群名称开始缩小范围。" />
          ) : (
            <div className="aw-community-grid">
              {response.communities.map((community) => (
                <CommunityCard community={community} key={community.id} />
              ))}
            </div>
          )}

          {response.num_pages > 1 && (
            <Pagination className="zhibi-pagination" current={response.page} total={response.total} pageSize={response.page_size} onChange={setPage} />
          )}
        </Card>
      </div>
    </ConfigProvider>
  );
}

function CommunityCard({ community }: { community: Community }) {
  return (
    <Card
      className="aw-community-card"
      actions={[<Link href={community.detail_url} key="detail">查看话题</Link>]}
    >
      <Flex className="aw-community-card-meta" justify="space-between" align="center" gap={8}>
        <Space size={6} wrap>
          <Tag color={community.is_scraping ? 'green' : 'default'}>{community.is_scraping ? '监测中' : '观察池'}</Tag>
          {community.is_merged && <Tag color="blue">已合并</Tag>}
        </Space>
        <Text className="aw-community-card-date" type="secondary">{formatDate(community.updated_date)}</Text>
      </Flex>
      <Title className="aw-community-card-title" level={4} ellipsis={{ rows: 2 }}><Link href={community.detail_url}>{community.display_name}</Link></Title>
      {community.merged_into && (
        <Paragraph className="aw-merged-note" type="secondary" ellipsis>
          已合并入 <Link href={community.merged_into.detail_url}>{community.merged_into.display_name}</Link>
        </Paragraph>
      )}
      <Paragraph className="aw-community-card-keywords" type="secondary" ellipsis={{ rows: 2 }}>
        {community.keywords.slice(0, 6).join(' · ') || '暂无关键词'}
      </Paragraph>
      <Space className="aw-community-card-tags" wrap size={[6, 6]}>
        {community.tags.slice(0, 4).map((tag) => <Tag key={tag}><a href={`/awareness/communities/?q=%23${encodeURIComponent(tag)}`}>#{tag}</a></Tag>)}
        {community.tags.length > 4 && <Tag>+{community.tags.length - 4}</Tag>}
        {community.tags.length === 0 && <Tag>未标注标签</Tag>}
      </Space>
      <Paragraph className="aw-card-foot" type="secondary">{memberCountText(community)}</Paragraph>
    </Card>
  );
}

function CommunityDetailApp({
  communityId,
  fallbackTitle,
  canDownloadMembers,
}: {
  communityId: string;
  fallbackTitle: string;
  canDownloadMembers: boolean;
}) {
  const [payload, setPayload] = useState<CommunityDetailResponse | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [selectedResult, setSelectedResult] = useState<TopicResult>({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetch(`/awareness/api/community/${encodeURIComponent(communityId)}/`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<CommunityDetailResponse>;
      })
      .then((data) => {
        setPayload(data);
        setSelectedTask(data.selected_task);
        setSelectedResult(data.selected_result ?? {});
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError('社群详情加载失败，请稍后刷新页面。');
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [communityId]);

  function selectTask(task: Task) {
    setSelectedTask(task);
    fetch(`/awareness/task/${encodeURIComponent(task.id)}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ ok: boolean; message?: string; task?: Task & { result: TopicResult } }>;
      })
      .then((data) => {
        if (!data.ok || !data.task) throw new Error(data.message || '任务结果不存在。');
        setSelectedTask(data.task);
        setSelectedResult(data.task.result ?? {});
      })
      .catch((err: Error) => setError(err.message || '任务结果加载失败。'));
  }

  if (loading) return <ConfigProvider theme={zhibiTheme} locale={zhCN}><div className="zhibi-react-page"><Spin tip="正在加载社群画像..." /></div></ConfigProvider>;
  if (error && !payload) return <ConfigProvider theme={zhibiTheme} locale={zhCN}><div className="zhibi-react-page"><Alert type="error" showIcon message={error} /></div></ConfigProvider>;
  if (!payload) return <ConfigProvider theme={zhibiTheme} locale={zhCN}><div className="zhibi-react-page"><Empty description="没有找到社群" /></div></ConfigProvider>;

  const community = payload.community;
  const topics = selectedResult.topics ?? [];
  const membersCsvUrl = `/awareness/community/${encodeURIComponent(String(community.id))}/members.csv`;
  const currentPageUrl = `${window.location.pathname}${window.location.search}`;
  const membersDownloadUrl = canDownloadMembers
    ? membersCsvUrl
    : `/accounts/login/?next=${encodeURIComponent(currentPageUrl)}`;

  return (
    <ConfigProvider theme={zhibiTheme} locale={zhCN}>
      <div className="zhibi-react-page">
        <Card className="zhibi-hero-card aw-community-hero">
          <Flex className="zhibi-page-nav" justify="space-between" align="center" gap={12} wrap="wrap">
            <Breadcrumb
              items={[
                { title: <a href="/">首页</a> },
                { title: '态势感知' },
                { title: <a href="/awareness/communities/">社群监测</a> },
                { title: '社群画像' },
              ]}
            />
            <Button href="/awareness/communities/" icon={<ArrowLeftOutlined />}>返回社群列表</Button>
          </Flex>
          <Flex className="aw-profile-header" justify="space-between" align="flex-start" gap={16} wrap="wrap">
            <div className="aw-profile-title">
              <Title level={2}>{community.display_name || fallbackTitle}</Title>
              <Space wrap>
                {community.is_merged && <Tag color="blue">已合并</Tag>}
                {community.tags.map((tag) => <Tag key={tag}><a href={`/awareness/communities/?q=%23${encodeURIComponent(tag)}`}>#{tag}</a></Tag>)}
                {community.tags.length === 0 && <Tag>未标注标签</Tag>}
              </Space>
              {community.merged_into && (
                <Paragraph className="aw-profile-merged-note" type="secondary">
                  已合并入 <Link href={community.merged_into.detail_url}>{community.merged_into.display_name}</Link>，当前页面保留历史话题分析结果。
                </Paragraph>
              )}
            </div>
            <div className="aw-profile-meta">
              <div className="aw-profile-stat">
                <Text type="secondary">状态</Text>
                <strong>{community.is_scraping ? '监测中' : '观察池'}</strong>
              </div>
              <div className="aw-profile-stat">
                <Text type="secondary">社群识别时间</Text>
                <strong>{formatDate(community.updated_date)}</strong>
              </div>
            </div>
          </Flex>

          <div className="aw-profile-content">
            <section className="aw-profile-section">
              <Text strong>社群关键词</Text>
              <Paragraph type="secondary" className="aw-hero-keywords">
                {community.keywords.join(' · ') || '暂无关键词'}
              </Paragraph>
            </section>
            <section className="aw-profile-section">
              <Flex justify="space-between" align="center" gap={12} wrap="wrap">
                <div>
                  <Text strong>成员数据</Text>
                  <Paragraph type="secondary" className="aw-member-copy">
                    {memberCountText(community)}，预览前 10 位有效成员。
                    {(community.excluded_screen_names_count ?? 0) > 0
                      ? ` 已排除 ${formatNumber(community.excluded_screen_names_count ?? 0)} 位异常或长期不活跃成员。`
                      : ''}
                  </Paragraph>
                </div>
                <Button href={membersDownloadUrl}>{canDownloadMembers ? '下载全部成员' : '登录后下载'}</Button>
              </Flex>
              <Space wrap className="aw-member-preview">
                {(community.screen_names ?? []).slice(0, 10).map((name) => (
                  <Tag key={name}><a href={`https://twitter.com/${encodeURIComponent(name)}`} target="_blank" rel="noreferrer">@{name}</a></Tag>
                ))}
              </Space>
            </section>
          </div>
        </Card>

        {error && <Alert className="zhibi-alert" type="error" showIcon message={error} />}

        {payload.tasks.length > 0 ? (
          <TopicPanel
            topics={topics}
            tasks={payload.tasks}
            selectedTask={selectedTask}
            selectedResult={selectedResult}
            tweetCount={selectedResult.tweet_count ?? selectedTask?.tweet_count ?? 0}
            onSelectTask={selectTask}
          />
        ) : (
          <Empty description="暂无周期话题任务。" />
        )}

      </div>
    </ConfigProvider>
  );
}

function TaskPicker({ tasks, selectedTaskId, onSelect }: { tasks: Task[]; selectedTaskId: string; onSelect: (task: Task) => void }) {
  return (
    <Select
      showSearch
      className="aw-period-select"
      value={selectedTaskId || undefined}
      placeholder="切换话题周期"
      optionFilterProp="label"
      onChange={(value) => {
        const task = tasks.find((item) => item.id === value);
        if (task) onSelect(task);
      }}
      options={tasks.map((task) => ({
        value: task.id,
        label: `${task.since} 至 ${task.until}`,
      }))}
    />
  );
}

function TopicPanel({
  topics,
  tasks,
  selectedTask,
  selectedResult,
  tweetCount,
  onSelectTask,
}: {
  topics: Topic[];
  tasks: Task[];
  selectedTask: Task | null;
  selectedResult: TopicResult;
  tweetCount: number;
  onSelectTask: (task: Task) => void;
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filterText, setFilterText] = useState('');
  const sortedTopics = useMemo(() => [...topics].sort((a, b) => (b.Count ?? 0) - (a.Count ?? 0)), [topics]);
  const rankedTopics = useMemo(() => sortedTopics.map((topic, index) => ({ ...topic, rank: index + 1 })), [sortedTopics]);
  const filteredTopics = useMemo(() => {
    const keyword = filterText.trim().toLowerCase();
    if (!keyword) return rankedTopics;
    return rankedTopics.filter((topic) => [
      topic.rank,
      topic.Topic,
      topic.Count,
      topic.CustomName,
    ].some((value) => String(value ?? '').toLowerCase().includes(keyword)));
  }, [filterText, rankedTopics]);
  const pagedTopics = filteredTopics.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterText, topics]);

  const columns: ColumnsType<Topic & { rank: number }> = [
    { title: '#', dataIndex: 'rank', width: 80 },
    { title: '频率', dataIndex: 'Count', width: 120, render: (value?: number) => formatNumber(value) },
    { title: '话题', dataIndex: 'CustomName', render: (value?: string) => value || '未命名话题' },
  ];

  const warnings = selectedResult.quality?.warnings ?? [];

  return (
    <Card className="aw-topic-card">
      <Flex className="aw-topic-toolbar" justify="space-between" gap={16} wrap="wrap" align="center">
        <div>
          <Text type="secondary">话题分析</Text>
          <Title level={3}>话题分布</Title>
        </div>
        <div className="aw-topic-controls">
          <div>
            <Text type="secondary">话题周期</Text>
            {tasks.length > 0 && (
              <TaskPicker tasks={tasks} selectedTaskId={selectedTask?.id ?? ''} onSelect={onSelectTask} />
            )}
          </div>
          <Statistic title="推文数" value={tweetCount} />
          <Statistic title="话题数" value={selectedTask?.topic_count ?? sortedTopics.length} />
        </div>
      </Flex>
      {selectedTask?.quality_status && (
        <Space wrap style={{ marginBottom: 16 }}>
          <Tag color={selectedTask.quality_status === 'warning' ? 'gold' : 'green'}>
            质量：{selectedTask.quality_status === 'warning' ? '需复核' : selectedTask.quality_status}
          </Tag>
        </Space>
      )}
      {warnings.length > 0 && (
        <Alert
          className="zhibi-alert"
          type="warning"
          showIcon
          message={warnings.join('；')}
        />
      )}
      {topics.length > 0 ? (
        <Table
          rowKey={(record) => `${record.Topic ?? record.rank}-${record.CustomName ?? record.rank}`}
          size="small"
          columns={columns}
          dataSource={pagedTopics}
          footer={() => (
            <div className="aw-topic-table-footer">
              <label className="aw-topic-filter">
                <span>筛选</span>
                <Input
                  allowClear
                  size="small"
                  value={filterText}
                  onChange={(event) => setFilterText(event.target.value)}
                  placeholder="输入关键词"
                />
              </label>
              <Pagination
                className="aw-topic-pagination"
                current={currentPage}
                pageSize={pageSize}
                pageSizeOptions={[10, 20, 50, 100]}
                showSizeChanger
                showTotal={(total) => `共 ${formatNumber(total)} 个话题`}
                total={filteredTopics.length}
                onChange={(page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                }}
              />
            </div>
          )}
          pagination={false}
        />
      ) : (
        <Empty
          description={
            selectedTask?.last_error
              ? selectedTask.last_error
              : '当前周期尚未产出可展示的话题结果。'
          }
        />
      )}
    </Card>
  );
}

const emptyAlertList: EventAlertListResponse = {
  page: 1,
  page_size: 20,
  total: 0,
  num_pages: 1,
  summary: { open: 0, reviewed: 0, dismissed: 0, critical: 0, warning: 0, info: 0 },
  alerts: [],
};

const emptyClusterList: EventClusterListResponse = {
  page: 1,
  page_size: 10,
  total: 0,
  num_pages: 1,
  lifecycle: 'current',
  current_period_start: '',
  current_period_end: '',
  summary: {
    open: 0,
    reviewed: 0,
    dismissed: 0,
    local: 0,
    domain: 0,
    cross_domain: 0,
    global: 0,
    current: 0,
    new: 0,
    continuing: 0,
    history: 0,
    all: 0,
  },
  clusters: [],
};

const eventClusterDefaultPageSize = 12;
const eventClusterPageSizeOptions = [10, 12, 20, 50];

function readEventClusterPageSize() {
  const parsed = Number.parseInt(readQueryParam('page_size', String(eventClusterDefaultPageSize)), 10);
  return eventClusterPageSizeOptions.includes(parsed) ? parsed : eventClusterDefaultPageSize;
}

function EventAlertListApp() {
  const [clusterResponse, setClusterResponse] = useState<EventClusterListResponse>(emptyClusterList);
  const [page, setPage] = useState(Math.max(Number.parseInt(readQueryParam('page', '1'), 10) || 1, 1));
  const [pageSize, setPageSize] = useState(readEventClusterPageSize);
  const [query, setQuery] = useState(readQueryParam('q'));
  const [draft, setDraft] = useState(readQueryParam('q'));
  const [level, setLevel] = useState(readQueryParam('level'));
  const [lifecycle, setLifecycle] = useState(readQueryParam('lifecycle') || 'current');
  const [error, setError] = useState('');
  const [clusterLoading, setClusterLoading] = useState(true);
  const [clusterDetailLoading, setClusterDetailLoading] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState<EventClusterDetail | null>(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (level) params.set('level', level);
    if (lifecycle !== 'current') params.set('lifecycle', lifecycle);
    if (page > 1) params.set('page', String(page));
    if (pageSize !== eventClusterDefaultPageSize) params.set('page_size', String(pageSize));
    const selectedClusterId = selectedCluster?.id;
    if (selectedClusterId) params.set('cluster', String(selectedClusterId));
    window.history.replaceState(null, '', params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname);
  }, [level, lifecycle, page, pageSize, query, selectedCluster?.id]);

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (query) params.set('q', query);
    if (level) params.set('scope_level', level);
    if (lifecycle) params.set('lifecycle', lifecycle);
    setClusterLoading(true);
    setError('');
    fetch(`/awareness/api/event-clusters/?${params}`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<EventClusterListResponse>;
      })
      .then((payload) => setClusterResponse(payload))
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError('自动识别事件加载失败，请稍后刷新页面。');
      })
      .finally(() => setClusterLoading(false));
    return () => controller.abort();
  }, [level, lifecycle, page, pageSize, query]);

  useEffect(() => {
    const clusterId = readQueryParam('cluster');
    if (clusterId && !selectedCluster) {
      loadClusterDetail(Number(clusterId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function submitSearch() {
    setQuery(draft.trim());
    setPage(1);
  }

  function resetFilters() {
    setDraft('');
    setQuery('');
    setLevel('');
    setLifecycle('current');
    setPage(1);
  }

  function loadClusterDetail(clusterId: number) {
    setClusterDetailLoading(true);
    setError('');
    fetch(`/awareness/api/event-clusters/${encodeURIComponent(String(clusterId))}/`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ cluster: EventClusterDetail }>;
      })
      .then((payload) => {
        setSelectedCluster(payload.cluster);
      })
      .catch(() => setError('事件详情加载失败。'))
      .finally(() => setClusterDetailLoading(false));
  }

  const clusterColumns: ColumnsType<EventCluster> = [
    {
      title: '状态',
      dataIndex: 'lifecycle_label',
      width: 88,
      render: (_, record) => <Tag color={lifecycleColor(record.lifecycle_status)}>{record.lifecycle_label}</Tag>,
    },
    {
      title: '事件等级',
      dataIndex: 'level_label',
      width: 126,
      render: (_, record) => <Tag color={scopeColor(record.scope_level)}>{record.level_label || record.scope_label}</Tag>,
    },
    {
      title: '事件名称',
      dataIndex: 'title',
      render: (_, record) => (
        <button className="aw-alert-title-button" type="button" onClick={() => loadClusterDetail(record.id)}>
          <strong>{record.title}</strong>
          <span>{record.summary || (record.topic_signature?.keywords ?? []).join('，')}</span>
        </button>
      ),
    },
    {
      title: '社群',
      dataIndex: 'community_count',
      width: 86,
      render: (value: number) => formatNumber(value),
    },
    {
      title: '首次出现',
      dataIndex: 'first_seen_at',
      width: 128,
      render: (value: string) => formatDate(value),
    },
    {
      title: '最近更新',
      dataIndex: 'latest_seen_at',
      width: 128,
      render: (value: string) => formatDate(value),
    },
  ];

  return (
    <ConfigProvider theme={zhibiTheme} locale={zhCN}>
      <div className="zhibi-react-page">
        <Card className="zhibi-hero-card">
          <Breadcrumb
            className="zhibi-page-nav"
            items={[
              { title: <a href="/">首页</a> },
              { title: '态势感知' },
              { title: '事件预警' },
            ]}
          />
          <Title level={2}>事件预警</Title>
          <Paragraph type="secondary">基于持续监测社群的每周话题结果，按 CCAD 社群条件异常检测识别事件信号，并自动划分社群级、领域级、跨领域和全球性事件。</Paragraph>
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}><Card><Statistic title="当前事件" value={clusterResponse.summary.current} prefix={<ClusterOutlined />} /></Card></Col>
          <Col xs={24} md={8}><Card><Statistic title="本批新增" value={clusterResponse.summary.new} prefix={<WarningOutlined />} /></Card></Col>
          <Col xs={24} md={8}><Card><Statistic title="历史归档" value={clusterResponse.summary.history} prefix={<ExclamationCircleOutlined />} /></Card></Col>
        </Row>

        <Card className="zhibi-section-card">
          <Flex gap={12} wrap="wrap" align="center">
            <Segmented
              value={lifecycle}
              onChange={(value) => { setLifecycle(String(value)); setPage(1); }}
              options={[
                { value: 'current', label: `当前事件 ${formatNumber(clusterResponse.summary.current)}` },
                { value: 'continuing', label: `持续关注 ${formatNumber(clusterResponse.summary.continuing)}` },
                { value: 'history', label: `历史归档 ${formatNumber(clusterResponse.summary.history)}` },
                { value: 'all', label: `全部 ${formatNumber(clusterResponse.summary.all)}` },
              ]}
            />
            <Input.Search
              allowClear
              enterButton="检索"
              prefix={<SearchOutlined />}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onSearch={submitSearch}
              placeholder="搜索事件名称或摘要"
              className="aw-alert-search"
            />
            <Select
              allowClear
              className="aw-alert-filter"
              placeholder="事件等级"
              value={level || undefined}
              onChange={(value) => { setLevel(value ?? ''); setPage(1); }}
              options={[
                { value: 'local', label: '社群级事件' },
                { value: 'domain', label: '领域级事件' },
                { value: 'cross_domain', label: '跨领域事件' },
                { value: 'global', label: '全球性事件' },
              ]}
            />
            <Button onClick={resetFilters}>清空</Button>
            {clusterLoading && <Spin />}
          </Flex>
        </Card>

        {error && <Alert className="zhibi-alert" type="error" showIcon message={error} />}

        <Card className="zhibi-section-card">
          <Flex justify="space-between" align="center" gap={12} wrap="wrap">
            <div>
              <Text type="secondary">自动识别事件</Text>
              <Title level={3}>事件列表</Title>
            </div>
            <Tag color="blue">共 {formatNumber(clusterResponse.total)} 个</Tag>
          </Flex>
          <Table
            rowKey="id"
            size="small"
            columns={clusterColumns}
            dataSource={clusterResponse.clusters}
            loading={clusterLoading}
            pagination={false}
            locale={{ emptyText: <Empty description="暂无自动识别事件。" /> }}
          />
          {clusterResponse.total > 0 && (
            <Pagination
              className="zhibi-pagination"
              current={clusterResponse.page}
              total={clusterResponse.total}
              pageSize={clusterResponse.page_size}
              showSizeChanger
              pageSizeOptions={eventClusterPageSizeOptions}
              showTotal={(total) => `共 ${formatNumber(total)} 个事件`}
              onChange={(nextPage, nextPageSize) => {
                const pageSizeChanged = nextPageSize !== pageSize;
                setPageSize(nextPageSize);
                setPage(pageSizeChanged ? 1 : nextPage);
              }}
            />
          )}
        </Card>

        <EventClusterDrawer
          cluster={selectedCluster}
          loading={clusterDetailLoading}
          onClose={() => setSelectedCluster(null)}
        />
      </div>
    </ConfigProvider>
  );
}

function EventClusterDrawer({
  cluster,
  loading,
  onClose,
}: {
  cluster: EventClusterDetail | null;
  loading: boolean;
  onClose: () => void;
}) {
  const signalColumns: ColumnsType<EventSignal> = [
    {
      title: '社群',
      dataIndex: ['community', 'display_name'],
      width: 180,
      render: (_, record) => <Link href={record.community.detail_url}>{record.community.display_name}</Link>,
    },
    {
      title: '来源线索',
      dataIndex: 'title',
      render: (_, record) => (
        <div className="aw-source-line">
          <strong>{record.title}</strong>
          <span>{record.period_label} / 当前占比 {formatPercent(record.topic_weight)}</span>
        </div>
      ),
    },
    {
      title: '强度',
      dataIndex: 'score',
      width: 84,
      render: (value: number) => <Text strong>{value.toFixed(1)}</Text>,
    },
  ];

  return (
    <Drawer
      width={820}
      open={Boolean(cluster)}
      title={cluster?.title ?? '事件详情'}
      onClose={onClose}
      extra={cluster && (
        <Space>
          <Tag color={scopeColor(cluster.scope_level)}>{cluster.level_label || cluster.scope_label}</Tag>
          {cluster.status === 'reviewed' && <Tag color="green">已确认</Tag>}
        </Space>
      )}
    >
      {loading && <Spin />}
      {cluster && (
        <div className="aw-alert-detail">
          <Descriptions
            bordered
            size="small"
            column={2}
            items={[
              { key: 'lifecycle', label: '事件状态', children: <Tag color={lifecycleColor(cluster.lifecycle_status)}>{cluster.lifecycle_label}</Tag> },
              { key: 'scope', label: '事件等级', children: <Tag color={scopeColor(cluster.scope_level)}>{cluster.level_label || cluster.scope_label}</Tag> },
              { key: 'communities', label: '覆盖社群', children: formatNumber(cluster.community_count) },
              { key: 'first', label: '首次出现', children: formatDate(cluster.first_seen_at) },
              { key: 'latest', label: '最近出现', children: formatDate(cluster.latest_seen_at) },
            ]}
          />

          {cluster.summary && <Paragraph className="aw-alert-detail-summary">{cluster.summary}</Paragraph>}

          <Divider orientation="left">关键词</Divider>
          <Space wrap>
            {(cluster.evidence?.top_keywords ?? cluster.topic_signature?.keywords ?? []).map((keyword) => (
              <Tag key={keyword}>#{keyword}</Tag>
            ))}
          </Space>

          {!!cluster.evidence?.communities?.length && (
            <>
              <Divider orientation="left">覆盖社群</Divider>
              <Space wrap>
                {cluster.evidence.communities.map((community) => (
                  <Tag key={community.id}>
                    {community.display_name}
                    {typeof community.alert_score === 'number' ? ` ${community.alert_score.toFixed(1)}` : ''}
                  </Tag>
                ))}
              </Space>
            </>
          )}

          <Divider orientation="left">来源社群线索</Divider>
          <Table
            rowKey="id"
            size="small"
            columns={signalColumns}
            dataSource={cluster.signals ?? []}
            pagination={false}
            locale={{ emptyText: <Empty description="暂无来源社群线索。" /> }}
          />

          <Collapse
            ghost
            items={[
              {
                key: 'technical',
                label: '技术详情',
                children: (
                  <Descriptions
                    size="small"
                    column={2}
                    items={[
                      { key: 'display_rank', label: '展示排序分', children: Number(cluster.display_rank ?? 0).toFixed(1) },
                      { key: 'title_source', label: '命名来源', children: titleSourceLabel(cluster.metrics?.event_title_source) },
                      { key: 'score', label: '升级分数', children: cluster.escalation_score.toFixed(1) },
                      { key: 'quality', label: '事件质量分', children: Number(cluster.metrics?.event_quality_score ?? 0).toFixed(1) },
                      { key: 'noise', label: '噪声扣分', children: Number(cluster.metrics?.noise_penalty ?? 0).toFixed(1) },
                      { key: 'signals', label: '来源线索数', children: formatNumber(cluster.signal_count) },
                      { key: 'alerts', label: '社群信号数', children: formatNumber(cluster.alert_count) },
                      { key: 'consistency', label: '语义一致度', children: Number(cluster.metrics?.semantic_consistency ?? 0).toFixed(3) },
                      { key: 'velocity', label: '扩散速度', children: Number(cluster.metrics?.spread_velocity ?? 0).toFixed(3) },
                      { key: 'cluster_version', label: '聚合版本', children: cluster.cluster_version || '-' },
                    ]}
                  />
                ),
              },
            ]}
          />

          {(cluster.review_note || cluster.reviewed_by || cluster.reviewed_at) && (
            <>
              <Divider orientation="left">后台复核记录</Divider>
              <Descriptions
                size="small"
                column={1}
                items={[
                  { key: 'reviewed_by', label: '复核人', children: cluster.reviewed_by || '-' },
                  { key: 'reviewed_at', label: '复核时间', children: cluster.reviewed_at ? formatDate(cluster.reviewed_at) : '-' },
                  { key: 'review_note', label: '复核备注', children: cluster.review_note || '-' },
                ]}
              />
            </>
          )}
        </div>
      )}
    </Drawer>
  );
}

function EventAlertDrawer({
  alert,
  loading,
  onClose,
}: {
  alert: EventAlertDetail | null;
  loading: boolean;
  onClose: () => void;
}) {
  const topicColumns: ColumnsType<EventAlertTopic> = [
    { title: '话题', dataIndex: 'label', render: (value: string, record) => <Text strong={record.is_new}>{value || '未命名话题'}</Text> },
    { title: '当前占比', dataIndex: 'weight', width: 100, render: (value: number) => formatPercent(value) },
    { title: '历史占比', dataIndex: 'baseline_weight', width: 100, render: (value: number) => formatPercent(value) },
    { title: '变化', dataIndex: 'delta', width: 90, render: (value: number) => `${(value * 100).toFixed(1)}%` },
    { title: '类型', dataIndex: 'is_new', width: 90, render: (value: boolean) => <Tag color={value ? 'gold' : 'blue'}>{value ? '新话题' : '升温'}</Tag> },
  ];

  const signalColumns: ColumnsType<EventSignal> = [
    { title: '信号', dataIndex: 'title', render: (value: string) => <Text strong>{value || '未命名信号'}</Text> },
    { title: '分数', dataIndex: 'score', width: 90, render: (value: number) => value.toFixed(1) },
    { title: '当前占比', dataIndex: 'topic_weight', width: 100, render: (value: number) => formatPercent(value) },
    { title: '变化', dataIndex: 'topic_delta', width: 90, render: (value: number) => `${(value * 100).toFixed(1)}%` },
    {
      title: '证据',
      dataIndex: ['evidence', 'evidence_status'],
      width: 112,
      render: (_, record) => (
        <Tag color={(record.evidence?.representative_tweet_ids ?? []).length ? 'green' : 'default'}>
          {(record.evidence?.representative_tweet_ids ?? []).length ? '有代表推文' : '待补证据'}
        </Tag>
      ),
    },
  ];

  return (
    <Drawer
      width={760}
      open={Boolean(alert)}
      title={alert?.title ?? '事件预警详情'}
      onClose={onClose}
      extra={alert && <Tag color={severityColor(alert.severity)}>{alert.severity_label}</Tag>}
    >
      {loading && <Spin />}
      {alert && (
        <div className="aw-alert-detail">
          <Descriptions
            bordered
            size="small"
            column={2}
            items={[
              { key: 'community', label: '社群', children: <Link href={alert.community.detail_url}>{alert.community.display_name}</Link> },
              { key: 'period', label: '周期', children: alert.period_label },
              { key: 'score', label: '分数', children: alert.score.toFixed(1) },
              { key: 'status', label: '状态', children: <Tag color={statusColor(alert.status)}>{alert.status_label}</Tag> },
              { key: 'jsd', label: '分布偏移', children: (alert.metrics?.jsd ?? 0).toFixed(3) },
              { key: 'novelty', label: '新话题占比', children: formatPercent(alert.metrics?.novelty_mass) },
              { key: 'volume', label: '发文量 z-score', children: (alert.metrics?.volume_z ?? 0).toFixed(2) },
              { key: 'signal_count', label: '事件信号', children: formatNumber(alert.signal_count ?? alert.signals?.length ?? 0) },
              { key: 'detector', label: '检测器', children: alert.detector_version || '-' },
            ]}
          />

          <Divider orientation="left">触发原因</Divider>
          <Space wrap>
            {alert.reason_labels.map((label) => <Tag key={label}>{label}</Tag>)}
            {(alert.evidence?.top_keywords ?? []).map((keyword) => <Tag key={keyword}>#{keyword}</Tag>)}
          </Space>
          {alert.summary && <Paragraph className="aw-alert-detail-summary">{alert.summary}</Paragraph>}

          <Divider orientation="left">异常话题</Divider>
          <Table
            rowKey={(record, index) => `${record.source_topic_id ?? index}-${record.label}`}
            size="small"
            columns={topicColumns}
            dataSource={alert.topics ?? []}
            pagination={false}
          />

          <Divider orientation="left">事件信号</Divider>
          <Table
            rowKey="id"
            size="small"
            columns={signalColumns}
            dataSource={alert.signals ?? []}
            pagination={false}
            locale={{ emptyText: <Empty description="暂无事件信号。" /> }}
          />

          <Divider orientation="left">历史基线</Divider>
          <Descriptions
            size="small"
            column={2}
            items={[
              { key: 'period_count', label: '有效周期', children: alert.baseline?.period_count ?? '-' },
              { key: 'tweet_mean', label: '平均推文数', children: formatNumber(Math.round(alert.baseline?.tweet_count_mean ?? 0)) },
              { key: 'topic_mean', label: '平均话题数', children: (alert.baseline?.topic_count_mean ?? 0).toFixed(1) },
              { key: 'top1', label: '历史头部话题占比', children: formatPercent(alert.baseline?.top1_weight_mean) },
            ]}
          />
          {(alert.review_note || alert.reviewed_by || alert.reviewed_at) && (
            <>
              <Divider orientation="left">后台复核记录</Divider>
              <Descriptions
                size="small"
                column={1}
                items={[
                  { key: 'reviewed_by', label: '复核人', children: alert.reviewed_by || '-' },
                  { key: 'reviewed_at', label: '复核时间', children: alert.reviewed_at ? formatDate(alert.reviewed_at) : '-' },
                  { key: 'review_note', label: '复核备注', children: alert.review_note || '-' },
                ]}
              />
            </>
          )}
        </div>
      )}
    </Drawer>
  );
}

const listRoot = document.getElementById('awareness-community-list-root');
if (listRoot) {
  createRoot(listRoot).render(<CommunityListApp />);
}

const detailRoot = document.getElementById('awareness-community-detail-root');
if (detailRoot) {
  createRoot(detailRoot).render(
    <CommunityDetailApp
      communityId={detailRoot.dataset.communityId ?? ''}
      fallbackTitle={detailRoot.dataset.communityTitle ?? '社群详情'}
      canDownloadMembers={detailRoot.dataset.canDownloadMembers === 'true'}
    />,
  );
}

const eventAlertRoot = document.getElementById('awareness-event-alert-list-root');
if (eventAlertRoot) {
  createRoot(eventAlertRoot).render(
    <EventAlertListApp />,
  );
}
